import { useMemo, useRef, useState } from 'react';
import CSVReader from 'react-csv-reader';

import cs from 'classnames';
import { SHA1 } from 'crypto-js';

import useLoading from 'lib/hooks/useLoading';

import Button from 'components/shared/Button/Button';
import File from 'components/shared/File/File';
import { generateDescription } from 'components/shared/FileUploader/lib/helpers';
import Icon from 'components/shared/Icon/Icon';

const CSVUploader = ({
  title = 'Choose a file to import data from',
  parserOptions,
  handleFileLoaded
}) => {
  const [fileUploaded, setFileUploaded] = useState(false);
  const { loading } = useLoading();
  const fileInputRef = useRef(null);
  const handleClick = () => {
    fileInputRef.current.click();
  };
  const description = useMemo(() => generateDescription('csv'), []);

  const handleUpload = async (data, fileInfo) => {
    const fileId = SHA1(new Date().toString() + fileInfo.name).toString();
    setFileUploaded({
      fileId,
      name: fileInfo.name,
      size: fileInfo.size,
      uploadedSize: fileInfo.size,
      type: fileInfo.type,
      status: 'uploaded',
      progress: 100
    });
    await handleFileLoaded(data, fileInfo);
  };

  const handleDragOver = (e) => {
    if (loading) return;
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <div className={cs('flex w-full flex-col items-center gap-4 p-0')}>
      {!fileUploaded && (
        <>
          <div
            className={cs(
              'border-gray-400 flex h-[200px] w-full cursor-pointer flex-col items-center gap-5 rounded-lg border border-dashed p-8',
              'hover:border-primary-500 hover:bg-primary-25',
              'disabled:cursor-not-allowed disabled:opacity-50',
              loading && '!cursor-not-allowed opacity-50'
            )}
            onClick={handleClick}
            onDragOver={handleDragOver}>
            <Icon icon="cloud-upload" />
            <div className="flex flex-col items-center justify-center gap-1.5 p-0">
              <span className="text-gray-900 text-sm font-medium">{title}</span>
              <span className="text-gray-600 text-xs font-normal">{description}</span>
            </div>
            <Button
              outlined
              variant="tertiary"
              icon="export-mono"
              iconColor="primary"
              text="Upload"
              disabled={loading}
            />
          </div>
          <div className="hidden">
            <CSVReader
              ref={fileInputRef}
              label="Please select CSV Patient file."
              parserOptions={parserOptions}
              onFileLoaded={handleUpload}
            />
          </div>
        </>
      )}
      {fileUploaded && (
        <File
          key={fileUploaded.fileId}
          file={fileUploaded}
          onDelete={() => setFileUploaded(false)}
          status={fileUploaded.status}
        />
      )}
    </div>
  );
};

export default CSVUploader;
